import React, { useState, useEffect, useCallback } from 'react';
import Section from '../Section';
import InsightCard from './InsightCard';
import { Insight, ReviewStatus, insightsService } from '../../services/insightsService';

interface InsightsPanelProps {
  // Patient profile id — the same id used in the detail URL route.
  patientId: string | number;
}

type FilterValue = ReviewStatus | 'all';

const FILTERS: { value: FilterValue; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'unreviewed', label: 'Sin revisar' },
  { value: 'correct', label: 'Correctos' },
  { value: 'incorrect', label: 'Incorrectos' },
  { value: 'not_useful', label: 'No útiles' },
];

const InsightsPanel: React.FC<InsightsPanelProps> = ({ patientId }) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterValue>('all');

  const fetchInsights = useCallback(async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await insightsService.getPatientInsights(patientId, {
        review_status: filter,
        perpage: 100,
      });
      setInsights(res.results);
    } catch (err) {
      console.error('Error fetching insights:', err);
      setError('No se pudieron cargar los insights.');
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, filter]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Replace the reviewed insight in place; drop it if it no longer matches the active filter.
  const handleReviewed = (updated: Insight) => {
    setInsights((prev) => {
      if (filter !== 'all' && updated.review_status !== filter) {
        return prev.filter((i) => i.id !== updated.id);
      }
      return prev.map((i) => (i.id === updated.id ? updated : i));
    });
  };

  return (
    <Section style={{ marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#222' }}>Insights del paciente</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
          {FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                style={{
                  background: active ? '#38b2ac' : '#fff',
                  color: active ? '#fff' : '#475569',
                  border: `1px solid ${active ? '#38b2ac' : '#e2e8f0'}`,
                  borderRadius: 999,
                  padding: '5px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Cargando insights...</div>
      ) : error ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#e53e3e' }}>{error}</div>
      ) : insights.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>
          No hay insights {filter === 'unreviewed' ? 'sin revisar' : ''} para este paciente.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {insights.map((insight) => (
            <InsightCard key={insight.id} insight={insight} onReviewed={handleReviewed} />
          ))}
        </div>
      )}
    </Section>
  );
};

export default InsightsPanel;
